import { useEffect, useRef, useState } from "react";
import { Runtime, detectRuntime, hasNativeBridge } from "util/userAgent";
import { storeUrlForOS, trackStoreClick } from "constants/appStore";

/**
 * 하단 슬림 "앱 설치" 유도 배너 — DefaultLayout 이 하단 탭바가 있는 메인 화면에서만 마운트(광고 웹 유입자용).
 *  디자인 CSS 무수정(인라인 스타일만, AppToastHost 관례). 탭바 위에 고정.
 *  ★ 2026-10-21 스크롤하면 탭바만 내려가(slide-down) 배너가 공중에 떠 보였고, 채팅 플로팅 버튼(탭바 위 2rem)과 겹쳤다.
 *    → 탭바와 같이 내려가 화면 맨 아래에 붙고, 플로팅 버튼은 배너 높이만큼 올린다(.floating-wrap 인라인 bottom).
 *  ★ 2026-10-21 검은 바탕(#101116)이라 전역 span 색이 덮여 글씨가 안 보였다 → 흰 바탕 + 주황 글씨(색은 span 에 직접).
 *
 * ⚠️ 스토어 미공개 상태면 URL 이 비어 자동 휴면(렌더 안 함). 출시 후 constants/appStore.js 의
 *    STORE_RELEASED 를 true 로 바꾸면 활성화된다.
 */
const DISMISS_KEY = "muyeon.appBanner.dismissed"; // 값=YYYY-MM-DD (그 날은 숨김)

const todayStr = () => {
  try {
    return new Date().toISOString().slice(0, 10);
  } catch (e) {
    return "";
  }
};

const FLOATING_BOTTOM = "calc(8.2rem + 2rem)"; // _bottom-tab-bar.scss .floating-wrap 기본값

export default function AppInstallBanner() {
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === todayStr();
    } catch (e) {
      return false;
    }
  });
  const [tabHidden, setTabHidden] = useState(false);
  const boxRef = useRef(null);

  // 앱 내부(웹뷰)·설치완료(PWA)면 노출 안 함.
  const storeUrl = storeUrlForOS();
  const visible = !(hasNativeBridge() || detectRuntime() === Runtime.PWA) && !dismissed && !!storeUrl; // 미출시/미지원 OS → 휴면

  // 탭바 slide-down 따라가기 + 플로팅 버튼을 배너 위로.
  useEffect(() => {
    if (!visible) return undefined;
    const sync = () => {
      const bar = document.querySelector(".tab-bar-nav");
      setTabHidden(!!bar && bar.classList.contains("slide-down"));
      const floating = document.querySelector(".floating-wrap");
      const h = boxRef.current?.offsetHeight || 0;
      if (floating && h) floating.style.bottom = `calc(${FLOATING_BOTTOM} + ${h}px)`;
    };
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["class"] });
    return () => {
      observer.disconnect();
      const floating = document.querySelector(".floating-wrap");
      if (floating) floating.style.bottom = "";
    };
  }, [visible]);

  if (!visible) return null;

  const close = () => {
    try {
      localStorage.setItem(DISMISS_KEY, todayStr());
    } catch (e) {
      /* noop */
    }
    setDismissed(true);
  };
  const install = () => { trackStoreClick(storeUrl, "banner"); window.open(storeUrl, "_blank", "noopener"); };

  return (
    <div
      ref={boxRef}
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: "calc(env(safe-area-inset-bottom, 0px) + 8.2rem)", // 탭바($bottom-nav-tab-bar-height) 위
        zIndex: 14, // 탭바 아래·상단바(15) 이하
        transform: tabHidden ? "translateY(8.2rem)" : "translateY(0)", // 탭바가 내려가면 같이 내려가 맨 아래에 붙는다
        transition: "transform 0.35s ease-in-out", // 탭바(%base-fixed)와 같은 속도
        display: "flex",
        alignItems: "center",
        gap: "1rem",
        padding: "1rem 1.6rem",
        background: "#FFFFFF",
        borderTop: "1px solid #EEEEEE",
        boxShadow: "0 -2px 8px rgba(0, 0, 0, 0.06)",
      }}
    >
      <span style={{ fontSize: "1.3rem", fontWeight: 700, flex: 1, lineHeight: 1.3, color: "#F58232" }}>
        무용연 앱으로 더 편하게 이용하세요
      </span>
      <button
        type="button"
        onClick={install}
        style={{
          fontSize: "1.3rem",
          fontWeight: 700,
          color: "#FFFFFF",
          background: "#F58232",
          border: "none",
          borderRadius: "999px",
          padding: "0.7rem 1.4rem",
          whiteSpace: "nowrap",
        }}
      >
        설치
      </button>
      <button
        type="button"
        onClick={close}
        aria-label="닫기"
        style={{
          fontSize: "1.6rem",
          lineHeight: 1,
          color: "#999999",
          background: "transparent",
          border: "none",
          padding: "0.4rem",
        }}
      >
        ×
      </button>
    </div>
  );
}
